import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: { color: "bg-slate-500/15 text-slate-300 border-slate-500/30", icon: Lucide.FileText },
  sent: { color: "bg-blue-500/15 text-blue-300 border-blue-500/30", icon: Lucide.Send },
  paid: { color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Lucide.CheckCircle2 },
  cancelled: { color: "bg-rose-500/15 text-rose-300 border-rose-500/30", icon: Lucide.Ban },
};

const sym = (c) => (c === "INR" ? "₹" : "$");

export default function AdminInvoices() {
  const nav = useNavigate();
  const [tab, setTab] = useState("list");
  const [invoices, setInvoices] = useState([]);
  const [company, setCompany] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const loadInv = () => api.get("/admin/invoices").then((r) => setInvoices(r.data));
  const loadComp = () => api.get("/admin/invoices/company").then((r) => setCompany(r.data));
  useEffect(() => { loadInv(); loadComp(); }, []);

  const del = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_no}?`)) return;
    try { await api.delete(`/admin/invoices/${inv.id}`); toast.success("Deleted"); loadInv(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPDF = async (inv) => {
    const res = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = invoices.filter((inv) => {
    if (status && inv.status !== status) return false;
    if (q) {
      const h = `${inv.invoice_no} ${inv.customer?.name || ""} ${inv.customer?.email || ""}`.toLowerCase();
      if (!h.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="bx-heading text-3xl font-extrabold">Invoices</h1>
          <p className="text-slate-400 text-sm">Create branded invoices, record payments, collect signatures, send to customers.</p>
        </div>
        <button data-testid="invoice-new" onClick={() => nav("/admin/invoices/new")} className="bx-btn-primary"><Lucide.Plus size={14} /> New invoice</button>
      </div>

      <div className="flex gap-2 mt-5 flex-wrap" data-testid="invoice-tabs">
        <button onClick={() => setTab("list")} className={`bx-chip ${tab === "list" ? "ring-1 ring-purple-400" : ""}`}>Invoices · {invoices.length}</button>
        <button onClick={() => setTab("branding")} className={`bx-chip ${tab === "branding" ? "ring-1 ring-purple-400" : ""}`}>Branding</button>
      </div>

      {tab === "list" && (
        <>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Lucide.Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice no / customer…" className="bx-input pl-9 text-sm" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="bx-input text-sm w-40">
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button onClick={loadInv} className="bx-btn-ghost text-xs"><Lucide.RefreshCw size={11} /> Refresh</button>
          </div>

          <div className="bx-card mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left p-3">No.</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Issued</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Payment</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const meta = STATUS[inv.status] || STATUS.draft;
                  const Icon = meta.icon;
                  return (
                    <tr key={inv.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`invoice-row-${inv.id.slice(0, 8)}`}>
                      <td className="p-3 bx-mono text-[11px] text-purple-300">{inv.invoice_no}</td>
                      <td className="p-3">
                        <div className="text-slate-100">{inv.customer?.name}</div>
                        <div className="text-[10px] text-slate-500">{inv.customer?.email}</div>
                      </td>
                      <td className="p-3 text-xs text-slate-400 whitespace-nowrap">{new Date(inv.issued_at || inv.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-right font-bold text-purple-300">{sym(inv.currency)}{Number(inv.computed?.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="p-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.color}`}><Icon size={10} /> {inv.status}</span></td>
                      <td className="p-3 text-[11px]">
                        {inv.payment ? (
                          <div>
                            <div className="text-slate-200">{inv.payment.method}</div>
                            {inv.payment.transaction_ref && <div className="bx-mono text-[10px] text-slate-500">{inv.payment.transaction_ref}</div>}
                          </div>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button onClick={() => nav(`/admin/invoices/${inv.id}`)} className="bx-btn-ghost text-xs" title="Open"><Lucide.Eye size={12} /></button>
                        <button onClick={() => downloadPDF(inv)} className="bx-btn-ghost text-xs ml-1" title="Download PDF"><Lucide.Download size={12} /></button>
                        <a href={`${window.location.origin}/invoice/view/${inv.public_token}`} target="_blank" rel="noopener noreferrer" className="bx-btn-ghost text-xs ml-1" title="Public view"><Lucide.ExternalLink size={12} /></a>
                        <button onClick={() => del(inv)} className="bx-btn-ghost text-xs ml-1 text-rose-300" title="Delete"><Lucide.Trash2 size={12} /></button>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && <tr><td colSpan={7} className="p-12 text-center text-slate-500 text-sm">No invoices yet. Click <strong>New invoice</strong> to create one.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "branding" && company && <BrandingPanel company={company} onSaved={loadComp} />}
    </AdminLayout>
  );
}

function BrandingPanel({ company, onSaved }) {
  const [form, setForm] = useState(company);
  const [busy, setBusy] = useState(false);

  const upload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300_000) { toast.error("Max 300 KB"); return; }
    const data = new FormData();
    data.append("file", file);
    const endpoint = field === "logo_data_url" ? "logo" : "signature";
    try {
      const r = await api.post(`/admin/invoices/company/${endpoint}`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: r.data.data_url }));
      toast.success(`${endpoint} uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/invoices/company", form);
      toast.success("Branding saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bx-card p-6 mt-4 max-w-3xl" data-testid="branding-panel">
      <h2 className="bx-heading text-lg font-bold">Invoice branding</h2>
      <p className="text-xs text-slate-400 mt-1">These details + logo + signature appear on every PDF you generate.</p>

      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <Field label="Company name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="company-name" />
        <Field label="GSTIN" value={form.gstin || ""} onChange={(v) => setForm({ ...form, gstin: v })} mono />
        <Field label="Phone" value={form.phone || ""} onChange={(v) => setForm({ ...form, phone: v })} />
        <Field label="Email" value={form.email || ""} onChange={(v) => setForm({ ...form, email: v })} />
        <Field label="Website" value={form.website || ""} onChange={(v) => setForm({ ...form, website: v })} />
        <Field label="PAID stamp label" value={form.stamp_label || ""} onChange={(v) => setForm({ ...form, stamp_label: v })} hint="Shown inside the diagonal PAID stamp." />
      </div>
      <div className="mt-4">
        <label className="text-xs text-slate-400">Address</label>
        <textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={3} className="bx-input mt-1 resize-y" maxLength={400} />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-white/5">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Company logo (PNG / JPG · max 300 KB)</label>
          {form.logo_data_url ? (
            <div className="rounded-xl border border-white/10 p-4 bg-white/[0.04]" style={{ background: "white" }}>
              <img src={form.logo_data_url} alt="logo" style={{ maxHeight: form.logo_height_px || 56 }} />
            </div>
          ) : <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-slate-500 text-xs">No logo uploaded</div>}
          <div className="flex items-center gap-2 mt-2">
            <label className="bx-btn-ghost text-xs cursor-pointer">
              <Lucide.Upload size={12} /> Upload logo
              <input data-testid="logo-upload" type="file" accept="image/png,image/jpeg" hidden onChange={(e) => upload(e, "logo_data_url")} />
            </label>
            <label className="text-xs text-slate-400 ml-2">Height (px):</label>
            <input type="number" min={20} max={200} value={form.logo_height_px || 56} onChange={(e) => setForm({ ...form, logo_height_px: parseInt(e.target.value) || 56 })} className="bx-input w-20 bx-mono text-xs" />
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-400 block mb-1">Authorised signature (PNG)</label>
          {form.signature_data_url ? (
            <div className="rounded-xl border border-white/10 p-4" style={{ background: "white" }}>
              <img src={form.signature_data_url} alt="signature" style={{ maxHeight: 60 }} />
            </div>
          ) : <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-slate-500 text-xs">No signature uploaded</div>}
          <label className="bx-btn-ghost text-xs cursor-pointer mt-2 inline-flex">
            <Lucide.Upload size={12} /> Upload signature
            <input data-testid="signature-upload" type="file" accept="image/png,image/jpeg" hidden onChange={(e) => upload(e, "signature_data_url")} />
          </label>
          <p className="text-[10px] text-slate-500 mt-1">Appears under {`"Authorised — ${form.name}"`} on every PDF.</p>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button data-testid="branding-save" onClick={save} disabled={busy} className="bx-btn-primary disabled:opacity-50">
          {busy ? <Lucide.Loader2 className="animate-spin" size={14} /> : <Lucide.Save size={14} />} Save branding
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, mono, hint, testId }) {
  return (
    <div>
      <label className="text-xs text-slate-400">{label}</label>
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={`bx-input mt-1 ${mono ? "bx-mono" : ""}`} />
      {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
